// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle - Each component does ONE thing
// 2. Separation of Concerns - Logic separated from presentation
// 3. Modular Architecture - Independent, reusable components
//
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

// Refer to docs/implementation/qa-criteria-system.md

import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Divider,
  Stack,
  Chip
} from '@mui/material';
import { Info } from '@mui/icons-material';

/**
 * TemplateStep Component (Step 1)
 *
 * First: choose criteria type (Matrix or Boolean).
 * Then: Start from scratch or copy from existing criteria (filtered by type).
 * Copy list = all criteria the user can access (same as main list), not public-only templates.
 */
const TemplateStep = ({
  criteriaType,
  onCriteriaTypeChange,
  templateMode,
  selectedTemplate,
  criteriaList,
  onTemplateModeChange,
  onTemplateSelect
}) => {
  const typeKey = criteriaType || 'matrix';
  const copySourceOptions = (criteriaList || []).filter(
    (c) => (c.criteria_type || 'matrix') === typeKey
  );

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Step 1: Choose Template
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Choose whether to create a Matrix (scoring) or Boolean (Yes/No) QA criteria, then start from scratch or copy an existing one.
      </Typography>

      <Card>
        <CardContent>
          {/* Criteria Type: Matrix vs Boolean */}
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Criteria type
          </Typography>
          <RadioGroup
            value={criteriaType || 'matrix'}
            onChange={(e) => onCriteriaTypeChange(e.target.value)}
            sx={{ mb: 3 }}
          >
            <FormControlLabel
              value="matrix"
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body1" fontWeight="medium">
                    Service Management (Matrix scoring)
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Score each metric (e.g. poor/fair/good/excellent) and aggregate.
                  </Typography>
                </Box>
              }
              sx={{ mb: 2, p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}
            />

            <FormControlLabel
              value="boolean"
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body1" fontWeight="medium">
                    Qualification Management (Yes/No)
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Evaluate each criterion as Yes/No from the conversation.
                  </Typography>
                </Box>
              }
              sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}
            />
          </RadioGroup>

          <Divider sx={{ my: 3 }} />

          {/* Source: Start from Scratch or Copy from Existing */}
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Source
          </Typography>
          <RadioGroup
            value={templateMode}
            onChange={(e) => onTemplateModeChange(e.target.value)}
          >
            <FormControlLabel
              value="scratch"
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body1" fontWeight="medium">
                    Start from Scratch
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Create a new QA criteria with empty {criteriaType === 'boolean' ? 'qualification items' : 'categories and metrics'}.
                  </Typography>
                </Box>
              }
              sx={{ mb: 2, p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}
            />

            <FormControlLabel
              value="copy"
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body1" fontWeight="medium">
                    Copy from Existing
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Copy from any {criteriaType === 'boolean' ? 'Qualification' : 'Matrix'} criteria you can access (team or shared), then adjust as needed.
                  </Typography>
                </Box>
              }
              sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}
            />
          </RadioGroup>

          {/* Template Selection (only if copy mode) */}
          {templateMode === 'copy' && (
            <>
              <Divider sx={{ my: 3 }} />

              <FormControl fullWidth>
                <InputLabel>Criteria to copy</InputLabel>
                <Select
                  value={selectedTemplate || ''}
                  onChange={(e) => onTemplateSelect(e.target.value)}
                  label="Criteria to copy"
                >
                  {copySourceOptions.length === 0 ? (
                    <MenuItem disabled>
                      No {criteriaType === 'boolean' ? 'Qualification' : 'Matrix'} criteria available — create one first or use &quot;Start from Scratch&quot;
                    </MenuItem>
                  ) : (
                    copySourceOptions.map((criteria) => (
                      <MenuItem key={criteria.id} value={criteria.id}>
                        {criteria.name}
                        {criteria.is_default && ' (Default)'}
                        {!criteria.is_active && ' - Inactive'}
                        <Chip
                          label={criteria.criteria_type === 'boolean' ? 'Yes/No' : 'Matrix'}
                          size="small"
                          color="primary"
                          sx={{ ml: 1 }}
                        />
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>

              {selectedTemplate && (() => {
                const template = criteriaList.find((c) => c.id === selectedTemplate);
                return template ? (
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Template Preview
                    </Typography>
                    <Stack direction="row" spacing={2} sx={{ mt: 1 }} flexWrap="wrap">
                      <Chip label={`${template.total_metrics} metrics`} size="small" />
                      {criteriaType === 'matrix' && (
                        <Chip
                          label={`${template.categories?.length || 0} categories`}
                          size="small"
                        />
                      )}
                      <Chip
                        label={template.is_active ? 'Active' : 'Inactive'}
                        size="small"
                        color={template.is_active ? 'success' : 'default'}
                      />
                    </Stack>
                    {template.description && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        {template.description}
                      </Typography>
                    )}
                  </Box>
                ) : null;
              })()}
            </>
          )}

          <Alert severity="info" icon={<Info />} sx={{ mt: 3 }}>
            <Typography variant="body2">
              {templateMode === 'scratch'
                ? criteriaType === 'boolean'
                  ? 'You will define qualification items (name, question, answer, mandatory) in the next steps.'
                  : 'You will define categories and metrics in the next steps.'
                : 'The selected template will be copied. You can modify any part in the following steps.'}
            </Typography>
          </Alert>
        </CardContent>
      </Card>
    </Box>
  );
};

export default TemplateStep;
